import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Close } from '@carbon/react/icons';
import styles from './expandable-container.module.scss';

export interface ExpandableControls {
  isExpanded: boolean;
  expand: () => void;
  collapse: () => void;
}

interface ExpandableContainerProps {
  children: (controls: ExpandableControls) => ReactNode;
  alignment?: 'center' | 'bottom';
  expandedClassName?: string;
  /**
   * Keep the children mounted across expand/collapse: expanding only restyles the
   * wrapper into a fixed modal instead of portaling (which would unmount and
   * remount the subtree). Use it when the children own data/chart state that must
   * not re-fetch or re-initialise on expand. The caller must render a stable tree
   * (same element positions in both states) for React to preserve the instances.
   * Default (portal) mode is unchanged for existing consumers.
   */
  keepMounted?: boolean;
}

export const ExpandableContainer = ({
  children,
  alignment = 'bottom',
  expandedClassName,
  keepMounted = false,
}: ExpandableContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const expand = useCallback(() => setIsExpanded(true), []);
  const collapse = useCallback(() => setIsExpanded(false), []);

  // Close on Escape key.
  useEffect(() => {
    if (!isExpanded) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  // Prevent body scrolling while expanded.
  useEffect(() => {
    if (!isExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isExpanded]);

  const controls: ExpandableControls = { isExpanded, expand, collapse };

  const contentClass = [
    styles.expandableContent,
    alignment === 'bottom'
      ? styles.expandableContentBottom
      : styles.expandableContentCenter,
    expandedClassName,
  ]
    .filter(Boolean)
    .join(' ');

  // Keep-mounted mode: children stay at the same React position always; expanding
  // just swaps the wrapper classes (collapsed → `display: contents`, transparent;
  // expanded → fixed modal box). No portal, so nothing unmounts/remounts — the
  // children keep their state and don't re-fetch. The backdrop + box are in-place
  // fixed siblings; no ancestor here establishes a stacking/transform context, so
  // they layer and escape overflow correctly.
  if (keepMounted) {
    return (
      <>
        {isExpanded && (
          <div className={styles.expandableBackdrop} onClick={collapse} />
        )}
        <div className={isExpanded ? contentClass : styles.keptCollapsed}>
          {isExpanded && (
            <button
              type="button"
              className={styles.expandableCloseButton}
              onClick={collapse}
              aria-label="Close expanded view"
            >
              <Close size={20} />
            </button>
          )}
          <div
            className={
              isExpanded ? styles.expandableBody : styles.keptCollapsed
            }
          >
            {children(controls)}
          </div>
        </div>
      </>
    );
  }

  if (!isExpanded) {
    return <>{children(controls)}</>;
  }

  return (
    <>
      {createPortal(
        <div className={styles.expandableOverlay}>
          <div className={styles.expandableBackdrop} onClick={collapse} />
          <div className={contentClass}>
            <button
              type="button"
              className={styles.expandableCloseButton}
              onClick={collapse}
              aria-label="Close expanded view"
            >
              <Close size={20} />
            </button>
            <div className={styles.expandableBody}>{children(controls)}</div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
